import { useEffect } from 'react'
import { Vector3 } from 'three'

import { useCADPageStore } from '@/state'
import { useCADQuery } from '@/services/queries/cads'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { getOperationStepBoundingBox } from '@/lib/cad/boundingBoxes/getOperationStepBoundingBox'
import { getTargetAssemblyGroupIds } from '@/lib/cad/operationSteps/getTargetAssemblyGroupIds'

const getTargetState = (nextTarget: Vector3) => {
  const { cameraPosition, target } = useCADPageStore.getState()
  const nextTargetArray = nextTarget.toArray()

  if (target.length !== 3 || cameraPosition.length !== 3) {
    return {
      target: nextTargetArray,
    }
  }

  const previousTarget = new Vector3().fromArray(target)
  const targetDelta = nextTarget.clone().sub(previousTarget)

  if (targetDelta.lengthSq() < 1e-18) {
    return {
      target: nextTargetArray,
    }
  }

  return {
    cameraPosition: new Vector3()
      .fromArray(cameraPosition)
      .add(targetDelta)
      .toArray(),
    target: nextTargetArray,
  }
}

export const useOperationStepTarget = () => {
  const {
    data: { gltf },
  } = useCADQuery()
  const { steps } = useOperationSteps()

  const operationStep = useCADPageStore((state) => state.operationStep)
  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const hidePreviousOperations = useCADPageStore(
    (state) => state.hidePreviousOperations,
  )
  const showFullModel = useCADPageStore((state) => state.showFullModel)
  const sceneCenter = useCADPageStore((state) => state.sceneCenter)
  const setCadPageState = useCADPageStore((state) => state.setState)

  useEffect(() => {
    if (!operationStep) {
      if (sceneCenter) {
        setCadPageState(getTargetState(sceneCenter))
      }
      return
    }

    if (!gltf) return

    if (showFullModel) {
      if (sceneCenter) {
        setCadPageState(getTargetState(sceneCenter))
      }
      return
    }

    const activeStep = steps.find((step) => step.id === operationStep.stepId)
    if (!activeStep) return

    const assemblyGroupIds = getTargetAssemblyGroupIds({
      activeStep,
      hidePreviousOperations,
      steps,
    })

    const boundingBox = getOperationStepBoundingBox({
      assemblyGroupIds,
      gltf,
      hiddenParts,
    })

    const target = boundingBox?.getCenter(new Vector3()) ?? sceneCenter
    if (!target) return

    setCadPageState(getTargetState(target))
  }, [
    gltf,
    hiddenParts,
    hidePreviousOperations,
    operationStep,
    sceneCenter,
    setCadPageState,
    showFullModel,
    steps,
  ])
}
